import { action, computed, makeObservable, observable } from 'mobx'

import { IRootStore } from 'store/types'

import { ELockConnectInfoDTO, ELockFileItem, ELockPhoto, OrderELockInfoDTO } from '../types'
import { ElockReasonModel } from './Reason/ElockReasonModel'
import { ELockApiStore } from './ELockApiStore'

export class ELockUiStore {
  @observable eLockName = ''
  @observable isActive = false
  @observable eLockConnectInfo: ELockConnectInfoDTO | null = null

  @observable eLockReason = new ElockReasonModel()

  @observable isOnline: boolean = true

  /** Фото двери изнутри (с установленным замком) */
  @observable doorFromInsidePhoto: ELockFileItem = {
    url: '',
    type: ELockPhoto.doorFromInside,
    size: 0,
  }
  /** Фото двери с торца (с установленным замком) */
  @observable doorFromTheEndPhoto: ELockFileItem = {
    url: '',
    type: ELockPhoto.doorFromTheEnd,
    size: 0,
  }
  /**  Фотография контроллера с его номером */
  @observable controllerPhoto: ELockFileItem = { url: '', type: ELockPhoto.controller, size: 0 }

  @observable readonly apiStore = new ELockApiStore(this)

  constructor(readonly rootStore: IRootStore) {
    makeObservable(this)
  }

  get viewOrderUiStore() {
    return this.rootStore.viewOrderUiStore
  }

  get orderId() {
    return this.rootStore.viewOrderUiStore.selectedOrder?.id
  }

  get order() {
    return this.rootStore.viewOrderUiStore.selectedOrder
  }

  get userId() {
    return this.rootStore.appStore.user.info.user_id
  }

  @computed
  get photos(): ELockFileItem[] {
    return [this.doorFromInsidePhoto, this.doorFromTheEndPhoto, this.controllerPhoto]
  }

  @computed
  get invalidPhotos() {
    const photos = [this.doorFromInsidePhoto, this.doorFromTheEndPhoto, this.controllerPhoto]
    return photos.some((photo) => !photo.url || photo.url.length === 0)
  }

  initialize = (value: OrderELockInfoDTO) => {
    const photos = value.photos
      .filter((photo) => photo.type !== ELockPhoto.reason)
      .map((photo) => ({ ...photo, size: 0 }))

    this.setELockPhotos(photos)
    this.setELockName(value.elockName)
    this.setIsActive(value.enabled)

    this.eLockReason.initialize(value)
  }

  @action
  setELockName = (value: string) => {
    this.eLockName = value
  }

  @action
  setIsActive = (value: boolean) => {
    this.isActive = value
  }

  @action
  setPhotoData = (key: ELockPhoto) =>
    action((item: Omit<ELockFileItem, 'type'>) => {
      if (key !== ELockPhoto.reason) {
        this[key].url = item.url
        this[key].size = item.size
        this[key].type = key
      }
    })

  @action
  setIsOnline = (value: boolean) => {
    this.isOnline = value
  }

  @action
  setELockConnectInfo = (value: ELockConnectInfoDTO | null) => {
    this.eLockConnectInfo = value
  }

  @action
  setELockPhotos = (value: ELockFileItem[]) => {
    for (const photo of value) {
      this.setPhotoData(photo.type as ELockPhoto)(photo)
    }
  }

  @action
  reset = () => {
    this.setELockConnectInfo(null)
    this.setELockName('')
    this.doorFromInsidePhoto.url = ''
    this.doorFromTheEndPhoto.url = ''
    this.controllerPhoto.url = ''

    this.eLockReason.reset()
  }

  async getELockConnectInfo() {
    return await this.apiStore.getELockConnectInfo()
  }

  async checkNetwork() {
    return await this.apiStore.checkNetwork()
  }

  async activateELock() {
    return await this.apiStore.activateELock()
  }

  async updateELock() {
    return await this.apiStore.updateELock()
  }
}
